// @flow

import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'
import authorization from 'live/services/authorization'
import xhr from 'live/services/xhr'

const requestCommon = {
    credentials: 'same-origin',
    cache: 'no-cache',
    redirect: 'error'
}

const jsonHeaders = {
    'Content-Type': 'application/json'
}

const ACTIVITY_LOG_PREFIX_URL = '/services/plugin-petrobras/activity_log'
const ACTIVITY_LOG_CONFIG_PREFIX_URL = '/services/plugin-petrobras/activity_log/config'

function fetchPickList (listName) {
    return fetch(ACTIVITY_LOG_PREFIX_URL + '/' + listName, { ...requestCommon, headers: jsonHeaders }).then(
        response => {
            if (!response.ok) throw response

            return response.json()
        }
    )
}

export function fetchCSDs () {
    return fetchPickList('csds')
}

export function fetchCsdFromUser () {
    const LOOKUP_NAME = 'user_csd'

    return fetch(`/rest/lookup/${LOOKUP_NAME}`, {
        ...requestCommon,
        headers: jsonHeaders
    })
        .then(response => {
            // return empty array for 40x
            if (!response.ok) return []

            return response.json()
        })
        .then(({ entries }) => {
            const userEntry = entries.find(e => e.key === authorization.user.username)

            if (userEntry) return userEntry.value

            return null
        })
}

export function deleteActivitylogById (id: number) {
    let url = `${ACTIVITY_LOG_PREFIX_URL}/${id}`

    return fetch(url, {
        ...requestCommon,
        headers: jsonHeaders,
        method: 'DELETE'
    }).then(response => {
        if (!response.ok) throw response

        return response.status === 204 ? null : response.json()
    })
}

export function fetchActivities (csdId) {
    let url = ACTIVITY_LOG_PREFIX_URL + '/csd/' + csdId + '/activities'

    return fetch(url, { ...requestCommon, headers: jsonHeaders }).then(response => {
        if (!response.ok) throw response

        return response.json()
    })
}

export function fetchInterventions () {
    let url = '/services/plugin-wells/drillingParamConfig/intervention_types'

    return fetchResource(url).then((response) => {
      let interventions = []
      if(response.configs && response.configs.length > 0)
        interventions = response.configs.map(({type, id}) => ({id: id, name: i18n(type)}))
      return interventions;
    })
}

export function fetchInitiatives () {
    return fetchPickList('initiatives')
}

export function fetchClientFunctions () {
    return fetchPickList('client_functions')
}

export function fetchDetailing () {
    return fetchPickList('detailing')
}

export function fetchDetailingFor (detailing1Id) {
    let url = `${ACTIVITY_LOG_PREFIX_URL}/detailing/${detailing1Id}/details`

    return fetch(url, { ...requestCommon, headers: jsonHeaders }).then(response => {
        if (!response.ok) throw response

        return response.json()
    })
}

export function fetchServiceNatureList () {
    return fetchPickList('service_nature')
}

export function fetchSmsGainList () {
    return fetchPickList('sms_gain')
}

export function fetchPerformanceGainList () {
    return fetchPickList('performance_gain')
}

function fetchResource (url) {
    return fetch(url, { ...requestCommon, headers: jsonHeaders }).then(response => {
        if (!response.ok) throw response

        return response.json()
    })
}

export function fetchWells () {
    let url = '/services/plugin-wells/wells'

    return fetchResource(url)
}

export function fetchRigs () {
    let url = '/services/plugin-wells/wells/available-rigs'

    return fetchResource(url)
}

export function fetchAssetRigs () {
    let url = '/services/plugin-liverig/assets/rig'

    return fetchResource(url)
}

export function fetchUsers () {
    let url = '/rest/user?sort=name'

    return fetchResource(url)
}

export function createOrUpdateActivityLog (activityLog) {
    let url = ACTIVITY_LOG_PREFIX_URL

    return xhr.post(url, JSON.stringify(activityLog))
        .then(response => {
            if (response.status !== 200) throw response

            return response.data
        })
}

export function fetchAllConfigFiles () {
    return Promise.all([
        fetchCSDsFile(),
        fetchActivitiesFile(),
        fetchCsdActivitiesAssociationFile(),
        fetchInterventionsFile(),
        fetchInitiativesFile(),
        fetchClientRolesFile(),
        fetchDetailing1File(),
        fetchDetailing2File(),
        fetchDetailingAssociationFile(),
        fetchServiceNatureFile(),
        fetchSmsGainFile(),
        fetchPerformanceGainFile()
    ]).then(arrayOfResults => {
        return {
            csdsFile: arrayOfResults[0],
            activitiesFile: arrayOfResults[1],
            csdActivitiesAssociationFile: arrayOfResults[2],
            interventionsFile: arrayOfResults[3],
            initiativesFile: arrayOfResults[4],
            clientRolesFile: arrayOfResults[5],
            detailing1File: arrayOfResults[6],
            detailing2File: arrayOfResults[7],
            detailingAssociationFile: arrayOfResults[8],
            serviceNatureFile: arrayOfResults[9],
            smsGainFile: arrayOfResults[10],
            performanceGainFile: arrayOfResults[11]
        }
    })
}

function fetchConfigFile (filePath) {
    let url = `${ACTIVITY_LOG_CONFIG_PREFIX_URL}/${filePath}`

    return fetch(url, { ...requestCommon, headers: jsonHeaders }).then(response => {
        if (!response.ok) throw response

        return response.json()
    })
}

function fetchCSDsFile () {
    return fetchConfigFile('csds')
}

function fetchActivitiesFile () {
    return fetchConfigFile('activities')
}

function fetchCsdActivitiesAssociationFile () {
    return fetchConfigFile('csd_activities')
}

function fetchInterventionsFile () {
    return fetchConfigFile('interventions')
}

function fetchInitiativesFile () {
    return fetchConfigFile('initiatives')
}

function fetchClientRolesFile () {
    return fetchConfigFile('client_roles')
}

function fetchDetailing1File () {
    return fetchConfigFile('detailing1')
}

function fetchDetailing2File () {
    return fetchConfigFile('detailing2')
}

function fetchDetailingAssociationFile () {
    return fetchConfigFile('detailing_association')
}

function fetchServiceNatureFile () {
    return fetchConfigFile('service_nature')
}

function fetchSmsGainFile () {
    return fetchConfigFile('sms_gain')
}

function fetchPerformanceGainFile () {
    return fetchConfigFile('performance_gain')
}

function saveConfigFile (filePath, json) {
    let url = `${ACTIVITY_LOG_CONFIG_PREFIX_URL}/${filePath}`

    return fetch(url, {
        ...requestCommon,
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify(json)
    }).then(response => {
        if (!response.ok) throw response

        return response
    })
}

export function saveAllConfigFiles (
    csdFile,
    activitiesFile,
    csdActivitiesFile,
    interventionsFile,
    initiativesFile,
    clientRolesFile,
    detailing1File,
    detailing2File,
    detailingAssociationFile,
    serviceNatureFile,
    smsGainFile,
    performanceGainFile
) {
    return Promise.all([
        saveCSDsFile(csdFile),
        saveActivitiesFile(activitiesFile),
        saveCsdActivitiesAssociationFile(csdActivitiesFile),
        saveInterventionsFile(interventionsFile),
        saveInitiativesFile(initiativesFile),
        saveClientRolesFile(clientRolesFile),
        saveDetailing1File(detailing1File),
        saveDetailing2File(detailing2File),
        saveDetailingAssociationFile(detailingAssociationFile),
        saveServiceNatureFile(serviceNatureFile),
        saveSmsGainFile(smsGainFile),
        savePerformanceGainFile(performanceGainFile)
    ]).then(arrayOfResults => {
        return arrayOfResults
    })
}

export function saveCSDsFile (json) {
    return saveConfigFile('csds', json)
}

export function saveActivitiesFile (json) {
    return saveConfigFile('activities', json)
}

export function saveCsdActivitiesAssociationFile (json) {
    return saveConfigFile('csd_activities', json)
}

export function saveInterventionsFile (json) {
    return saveConfigFile('interventions', json)
}

export function saveInitiativesFile (json) {
    return saveConfigFile('initiatives', json)
}

export function saveClientRolesFile (json) {
    return saveConfigFile('client_roles', json)
}

export function saveDetailing1File (json) {
    return saveConfigFile('detailing1', json)
}

export function saveDetailing2File (json) {
    return saveConfigFile('detailing2', json)
}

export function saveDetailingAssociationFile (json) {
    return saveConfigFile('detailing_association', json)
}

export function saveServiceNatureFile (json) {
    return saveConfigFile('service_nature', json)
}

export function saveSmsGainFile (json) {
    return saveConfigFile('sms_gain', json)
}

export function savePerformanceGainFile (json) {
    return saveConfigFile('performance_gain', json)
}
